// src/ui/screens/SelectContactScreen.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ContactItem } from '../../components/ContactComponents/ContactItem';
import { contactService } from '../../services/contact/contactService';
import { chatService } from '../../services/chat/chatService';
import { chatState } from '../../state/chat/chatState';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  blue: '#4DA3FF',
};

const SEARCH_BAR_HEIGHT = 60;

// ── SelectContactScreen ──────────────────────────────────────────────────────

export const SelectContactScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [wrytteContacts, setWrytteContacts] = useState([]);
  const [nonWrytteContacts, setNonWrytteContacts] = useState([]);
  const [filteredWrytte, setFilteredWrytte] = useState([]);
  const [filteredNonWrytte, setFilteredNonWrytte] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBarProgress, setSearchBarProgress] = useState(0);

  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── Load contacts ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadContacts = async () => {
      setIsLoading(true);
      setError('');

      try {
        const wrytte = await contactService.getFilatomContactsOptimized();
        const nonWrytte = await contactService.getNonFilatomContacts();

        setWrytteContacts(wrytte);
        setNonWrytteContacts(nonWrytte);
        setFilteredWrytte(wrytte);
        setFilteredNonWrytte(nonWrytte);
      } catch (err) {
        console.error('Load contacts error:', err);
        setError(err.message || 'Failed to load contacts');
      } finally {
        setIsLoading(false);
      }
    };

    loadContacts();
  }, []);

  // ── Search handler ─────────────────────────────────────────────────────────

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    const filteredW = wrytteContacts.filter(c =>
      c.formattedName.toLowerCase().includes(query)
    );
    const filteredN = nonWrytteContacts.filter(c =>
      c.formattedName.toLowerCase().includes(query)
    );
    setFilteredWrytte(filteredW);
    setFilteredNonWrytte(filteredN);
  }, [searchQuery, wrytteContacts, nonWrytteContacts]);

  // ── Scroll handler ─────────────────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const offset = scrollRef.current.scrollTop;
    const progress = Math.min(offset / SEARCH_BAR_HEIGHT, 1);
    setSearchBarProgress(progress);
  }, []);

  // ── Generate conversation ID ──────────────────────────────────────────────

  const generateConversationId = (id1, id2) => {
    const ids = [id1, id2].sort();
    return `${ids[0]}-${ids[1]}`;
  };

  // ── Navigate to chat ──────────────────────────────────────────────────────

  const navigateToChatScreen = async (contact) => {
    if (!contact.filatomUserId) return;

    const currentUserId = user?.uid || '';
    const conversationId = generateConversationId(
      currentUserId,
      contact.filatomUserId
    );

    // Initialize chat state
    await chatState.initialize();

    navigate(`/chat/${conversationId}`, {
      state: {
        receiverId: contact.filatomUserId,
        title: contact.formattedName,
        avatarUrl: contact.avatarUrl || null,
      },
    });
  };

  // ── Group by alphabet ──────────────────────────────────────────────────────

  const groupByAlphabet = (contacts) => {
    const grouped = {};
    for (const contact of contacts) {
      const letter = contact.formattedName ? contact.formattedName[0].toUpperCase() : '#';
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(contact);
    }
    const keys = Object.keys(grouped).sort();
    const result = {};
    for (const key of keys) {
      result[key] = grouped[key];
    }
    return result;
  };

  // ── Render section header ──────────────────────────────────────────────────

  const renderSectionHeader = (text) => {
    return (
      <div className="w-full px-4 py-1 bg-[#1A1A1A]">
        <span className="text-gray-400 text-sm font-semibold">{text}</span>
      </div>
    );
  };

  // ── Render action item ────────────────────────────────────────────────────

  const renderActionItem = (icon, title, onTap) => {
    return (
      <div className="w-full">
        <button
          onClick={onTap}
          className="w-full flex items-center px-4 py-3 hover:bg-filatom-card-hover transition-colors"
        >
          <svg className="w-7 h-7 text-filatom-blue flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {icon === 'group' && (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            )}
            {icon === 'person_add' && (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            )}
            {icon === 'channel' && (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            )}
          </svg>
          <span className="text-filatom-blue text-lg ml-3">{title}</span>
        </button>
        <div className="ml-[72px] h-px bg-white/5" />
      </div>
    );
  };

  // ── Build contact slivers ──────────────────────────────────────────────────

  const buildContactSlivers = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="w-8 h-8 border-2 border-filatom-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Finding your contacts on Filatom...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500 text-sm">Error: {error}</p>
        </div>
      );
    }

    if (filteredWrytte.length === 0 && filteredNonWrytte.length === 0) {
      return (
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400 text-sm">No contacts found</p>
        </div>
      );
    }

    const elements = [];

    // Wrytte contacts
    const wrytteGrouped = groupByAlphabet(filteredWrytte);
    for (const [letter, contacts] of Object.entries(wrytteGrouped)) {
      elements.push(renderSectionHeader(letter));
      for (const contact of contacts) {
        elements.push(
          <div key={contact.filatomUserId || contact.primaryPhone}>
            <ContactItem
              contact={contact}
              onTap={() => navigateToChatScreen(contact)}
            />
          </div>
        );
      }
    }

    // Non-Wrytte contacts
    if (filteredNonWrytte.length > 0) {
      elements.push(renderSectionHeader('Invite to Filatom'));
      const nonGrouped = groupByAlphabet(filteredNonWrytte);
      for (const [letter, contacts] of Object.entries(nonGrouped)) {
        elements.push(renderSectionHeader(letter));
        for (const contact of contacts) {
          elements.push(
            <div key={contact.primaryPhone}>
              <ContactItem
                contact={contact}
                showInviteButton={true}
                onTap={() => {}}
              />
            </div>
          );
        }
      }
    }

    return elements;
  };

  // ── Main render ───────────────────────────────────────────────────────────

  const statusBarHeight = 44;
  const headerHeight = statusBarHeight + 56 + SEARCH_BAR_HEIGHT + 8;
  const searchBarOffset = SEARCH_BAR_HEIGHT * searchBarProgress;
  const gradientHeight = headerHeight - searchBarOffset;

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* Content */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-screen overflow-y-auto"
        style={{ paddingTop: headerHeight, paddingBottom: 32 }}
      >
        {/* Action Items */}
        {renderActionItem('group', 'New group', () => {})}
        {renderActionItem('person_add', 'New contact', () => navigate('/new-contact'))}
        {renderActionItem('channel', 'New channel', () => {})}

        {/* Contacts */}
        {buildContactSlivers()}
      </div>

      {/* Gradient Overlay */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: gradientHeight,
          background: `linear-gradient(to bottom, rgba(8,9,11,0.95) 0%, rgba(8,9,11,0.75) 60%, rgba(8,9,11,0) 100%)`,
        }}
      />

      {/* Floating Header */}
      <div className="absolute top-0 left-0 right-0">
        {/* Top Bar */}
        <div className="flex items-center justify-between px-4 h-14" style={{ paddingTop: statusBarHeight }}>
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#0F1013' }}
          >
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-white text-lg font-bold">New chat</span>
          <div className="w-9" />
        </div>

        {/* Search Bar - slides up with scroll */}
        <div
          className="px-4 py-2 transition-all duration-200"
          style={{
            transform: `translateY(-${searchBarOffset}px)`,
            opacity: 1 - searchBarProgress,
          }}
        >
          <div
            className="relative h-11 rounded-full overflow-hidden"
            style={{ backgroundColor: COLORS.card }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-full bg-transparent text-white text-sm outline-none px-3"
              placeholder=""
            />
            {!searchQuery && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4.5 h-4.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-gray-400 text-sm">Search</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectContactScreen;